import { useCallback } from 'react';
import { subscriptionService } from '../services';
import { PlanType, ServiceType, CurrencyCode } from '../types/api';
import { AddressWithId } from '../services/addressService';

interface CreateSubscriptionParams {
  plan: PlanType;
  serviceType: ServiceType;
  isGift: boolean;
  selectedAddress: AddressWithId;
  subscriptionType: 'monthly' | 'yearly' | 'trial';
  weeklyDay: number;
  selectedTimeSlots: string[];
  currency: CurrencyCode;
  getEffectiveCode: () => { type: string; code: string } | null;
  // Gift-specific params
  recipientName?: string;
  recipientPhone?: string;
  giftMessage?: string;
  senderName?: string;
  senderPhone?: string;
}

export const useSubscriptionCreation = () => {
  const createSubscription = useCallback(async (params: CreateSubscriptionParams) => {
    const {
      plan,
      serviceType,
      isGift,
      selectedAddress,
      subscriptionType,
      weeklyDay,
      selectedTimeSlots,
      currency,
      getEffectiveCode,
      recipientName,
      recipientPhone,
      giftMessage,
      senderName,
      senderPhone
    } = params;

    let subscription;
    
    if (isGift) {
      // For gift subscription - Get recipient info from address
      const recipientNameFromAddress = 
        selectedAddress && 'recipientName' in selectedAddress ? (selectedAddress as any).recipientName : 
        selectedAddress && 'contactName' in selectedAddress ? (selectedAddress as any).contactName :
        selectedAddress && 'name' in selectedAddress ? (selectedAddress as any).name : 
        selectedAddress && 'fullName' in selectedAddress ? (selectedAddress as any).fullName : 
        recipientName;
        
      const recipientPhoneFromAddress = 
        selectedAddress && 'recipientPhone' in selectedAddress ? (selectedAddress as any).recipientPhone : 
        selectedAddress && 'contactPhone' in selectedAddress ? (selectedAddress as any).contactPhone :
        selectedAddress && 'phone' in selectedAddress ? (selectedAddress as any).phone : 
        selectedAddress && 'phoneNumber' in selectedAddress ? (selectedAddress as any).phoneNumber : 
        recipientPhone;
        
      const giftParams = {
        plan,
        service_type: serviceType,
        recipient_name: recipientNameFromAddress || recipientName || '',
        recipient_phone: recipientPhoneFromAddress || recipientPhone || '',
        address_id: selectedAddress.id,
        message: giftMessage || undefined,
        sender_name: senderName || '',
        sender_phone: senderPhone || '',
        currency: currency
      };
        
      subscription = await subscriptionService.createDirectGift(giftParams);
    } else {
      // Get effective code (promotion or referral)
      const effectiveCode = getEffectiveCode();
      
      // For regular subscription
      subscription = await subscriptionService.subscribe(
        plan, 
        selectedAddress,
        {
          dayOfWeek: weeklyDay,
          timeSlots: selectedTimeSlots,
          currency: currency,
          referral_code: effectiveCode?.type === 'referral' ? effectiveCode.code : undefined
        },
        serviceType,
        subscriptionType
      );
      
      // Update delivery settings
      if (subscription && subscription.id) {
        try {
          await subscriptionService.updateDeliverySettings(
            subscription.id,
            {
              dayOfWeek: weeklyDay,
              timeSlots: selectedTimeSlots
            }
          );
        } catch (settingsError) {
          console.error('Error updating delivery settings:', settingsError);
          // Continue with payment processing even if settings update fails
        }
      }
    }

    if (!subscription || !subscription.id) {
      throw new Error('Failed to create subscription: Invalid response from server');
    }

    return subscription;
  }, []);

  return { createSubscription };
}; 